import { useState, useEffect, useCallback, useRef } from 'react';
import {
  getQueuedSegmentsForUpload,
  updateSegmentState,
  deleteQueuedSegment,
  getPendingUploadCount,
} from '~/lib/indexed-db';
import { uploadSegment } from '~/lib/api';

const CONCURRENT_UPLOADS = 2;
const UPLOAD_INTERVAL_MS = 1000; // Check for uploads every second
const MAX_RETRIES = 5;
const BASE_BACKOFF_MS = 1000;
const JITTER_MS = 500;

export function useUploader() {
  const [pendingCount, setPendingCount] = useState(0);
  const [uploadingCount, setUploadingCount] = useState(0);
  const isRunningRef = useRef(false);
  const intervalRef = useRef<number | null>(null);

  /**
   * Process upload queue
   */
  const processQueue = useCallback(async () => {
    if (isRunningRef.current) return;
    isRunningRef.current = true;

    try {
      // Get segments to upload
      const segments = await getQueuedSegmentsForUpload(CONCURRENT_UPLOADS);

      if (segments.length === 0) {
        isRunningRef.current = false;
        return;
      }

      setUploadingCount(segments.length);

      // Upload concurrently
      const uploadPromises = segments.map(async (segment) => {
        try {
          // Update state to uploading
          await updateSegmentState(segment.sessionId, segment.segmentIndex, {
            state: 'uploading',
          });

          // Upload
          await uploadSegment(
            segment.sessionId,
            segment.segmentIndex,
            segment.startMs,
            segment.endMs,
            segment.sha256,
            segment.wavBlob
          );

          // Mark as uploaded (will be cleaned up later)
          await updateSegmentState(segment.sessionId, segment.segmentIndex, {
            state: 'uploaded',
            nextRetryAt: Date.now(),
          });

          // Delete after successful upload
          setTimeout(async () => {
            await deleteQueuedSegment(segment.sessionId, segment.segmentIndex);
          }, 5000);

          console.log(
            `Uploaded segment ${segment.segmentIndex} for session ${segment.sessionId}`
          );
        } catch (error) {
          console.error(
            `Failed to upload segment ${segment.segmentIndex}:`,
            error
          );

          // Calculate retry
          const retryCount = segment.retryCount + 1;

          if (retryCount >= MAX_RETRIES) {
            // Give up after max retries
            await updateSegmentState(segment.sessionId, segment.segmentIndex, {
              state: 'failed',
              retryCount,
              nextRetryAt: Date.now() + 60000, // Try again in 1 minute
            });
          } else {
            // Schedule retry with exponential backoff
            const backoff = Math.min(
              60000,
              Math.pow(2, retryCount) * BASE_BACKOFF_MS
            );
            const jitter = Math.random() * JITTER_MS;
            const nextRetryAt = Date.now() + backoff + jitter;

            await updateSegmentState(segment.sessionId, segment.segmentIndex, {
              state: 'queued',
              retryCount,
              nextRetryAt,
            });
          }
        }
      });

      await Promise.allSettled(uploadPromises);

      setUploadingCount(0);
    } catch (error) {
      console.error('Error processing upload queue:', error);
    } finally {
      isRunningRef.current = false;
    }
  }, []);

  /**
   * Update pending count
   */
  const updatePendingCount = useCallback(async (sessionId?: string) => {
    if (sessionId) {
      const count = await getPendingUploadCount(sessionId);
      setPendingCount(count);
    }
  }, []);

  /**
   * Start uploader
   */
  const start = useCallback(() => {
    if (intervalRef.current !== null) return;

    // Process immediately
    processQueue();

    // Then process periodically
    intervalRef.current = window.setInterval(() => {
      processQueue();
    }, UPLOAD_INTERVAL_MS);
  }, [processQueue]);

  /**
   * Stop uploader
   */
  const stop = useCallback(() => {
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  // Auto-start uploader on mount
  useEffect(() => {
    start();

    return () => {
      stop();
    };
  }, [start, stop]);

  return {
    pendingCount,
    uploadingCount,
    updatePendingCount,
  };
}
